<template>
  <div
    ref="lineChart"
    class="line-chart-container" />
</template>

<script>
  import * as echarts from 'echarts'

  export default {
    name: 'LineChart',
    inject: {
      isContained: {
        default: () => true,
      },
    },
    props: {
      data: {
        type: Array,
        required: true,
      },
      xAxisLabels: {
        type: Array,
        required: true,
      },
      yAxisDataType: {
        type: String,
        required: true,
      },
      yAxisDataTypes: {
        type: Array,
        default: () => [],
      },
      yAxisDataFormats: {
        type: Array,
        default: () => [],
      },
    },
    data () {
      return {
        chartInstance: null,
      }
    },
    computed: {
      isContainerExpanded () {
        return this.isContained()
      },
      // Peak absolute value per measure across all dimension groups
      // e.g. { 0: 450000, 1: 16 } for Net Sales and Avg Check
      measureMaxValues () {
        let maxByMeasure = {}
        for (let line of this.data) {
          let measureIdx = line.measureIndex || 0
          let lineMax = Math.max(...line.data.filter((v) => v != null).map(Math.abs))
          if (maxByMeasure[measureIdx] === undefined || lineMax > maxByMeasure[measureIdx]) {
            maxByMeasure[measureIdx] = lineMax
          }
        }
        return maxByMeasure
      },
      // Assigns each measure to a y-axis index
      // With STACKED_LINE_CHARTS flag: groups by type, splits within type by scale (5x), N axes
      // Without flag: original dual-axis logic (max 2 axes)
      axisAssignments () {
        let measureIndices = Object.keys(this.measureMaxValues).map(Number).sort()
        if (measureIndices.length <= 1) {
          return {}
        }

        if (this.$hasFeatureFlag('STACKED_LINE_CHARTS')) {
          return this.multiAxisAssignments
        }
        return this.dualAxisAssignments
      },
      // Original: first type = left, everything else = right, scale-based fallback
      dualAxisAssignments () {
        let measureIndices = Object.keys(this.measureMaxValues).map(Number).sort()
        let hasTypeDifference = this.yAxisDataTypes.length > 1
          && new Set(this.yAxisDataTypes).size > 1

        if (hasTypeDifference) {
          let firstType = this.yAxisDataTypes[0]
          let assignments = {}
          for (let idx of measureIndices) {
            assignments[idx] = this.yAxisDataTypes[idx] === firstType ? 0 : 1
          }
          return assignments
        }

        let firstMax = this.measureMaxValues[measureIndices[0]] || 1
        let assignments = {}
        for (let idx of measureIndices) {
          let bigger = Math.max(firstMax, this.measureMaxValues[idx] || 1)
          let smaller = Math.max(Math.min(firstMax, this.measureMaxValues[idx] || 1), 0.001)
          assignments[idx] = (bigger / smaller) > 5 ? 1 : 0
        }
        return assignments
      },
      // New: each unique type gets its own axis, scale splitting within type groups
      multiAxisAssignments () {
        let measureIndices = Object.keys(this.measureMaxValues).map(Number).sort()
        let typeGroups = new Map()
        for (let idx of measureIndices) {
          let type = this.yAxisDataTypes[idx] || 'number'
          if (!typeGroups.has(type)) {
            typeGroups.set(type, [])
          }
          typeGroups.get(type).push(idx)
        }

        let assignments = {}
        let nextAxis = 0

        for (let [, indices,] of typeGroups) {
          let primaryAxis = nextAxis
          let firstMax = this.measureMaxValues[indices[0]] || 1

          let needsSplit = indices.some((idx) => {
            let bigger = Math.max(firstMax, this.measureMaxValues[idx] || 1)
            let smaller = Math.max(Math.min(firstMax, this.measureMaxValues[idx] || 1), 0.001)
            return (bigger / smaller) > 5
          })

          if (needsSplit) {
            let secondaryAxis = nextAxis + 1
            for (let idx of indices) {
              let bigger = Math.max(firstMax, this.measureMaxValues[idx] || 1)
              let smaller = Math.max(Math.min(firstMax, this.measureMaxValues[idx] || 1), 0.001)
              assignments[idx] = (bigger / smaller) > 5 ? secondaryAxis : primaryAxis
            }
            nextAxis += 2
          } else {
            for (let idx of indices) {
              assignments[idx] = primaryAxis
            }
            nextAxis++
          }
        }

        if (new Set(Object.values(assignments)).size <= 1) {
          return {}
        }

        return assignments
      },
      // True when measures are split across multiple axes
      hasMultipleAxes () {
        return new Set(Object.values(this.axisAssignments)).size > 1
      },
      // Format a value based on its data type and optional DataFormat config
      formatValueForType () {
        return (dataType, value, dataFormat) => {
          if (value === null || value === undefined) {
            return ''
          }
          if (dataType === 'money') {
            let num = value / 100
            let decimals = dataFormat?.decimal_places ?? 2
            let absNum = Math.abs(num)
            let localeOptions = {
              minimumFractionDigits: decimals,
              maximumFractionDigits: decimals,
            }
            if (dataFormat?.show_commas === false) {
              localeOptions.useGrouping = false
            }
            let formatted = absNum.toLocaleString(undefined, localeOptions)
            let prefix = dataFormat?.show_currency !== false ? '$' : ''
            if (num < 0) {
              return dataFormat?.parenthesize_negatives
                ? `(${prefix}${formatted})`
                : `-${prefix}${formatted}`
            }
            return `${prefix}${formatted}`
          }
          if (dataType === 'percentage') {
            let pct = value * 100
            let decimals = dataFormat?.decimal_places ?? 2
            let absPct = Math.abs(pct)
            let formatted = absPct.toFixed(decimals)
            if (pct < 0) {
              return dataFormat?.parenthesize_negatives
                ? `(${formatted}%)`
                : `-${formatted}%`
            }
            return formatted + '%'
          }
          if (dataType === 'duration' && (dataFormat?.mm_ss || dataFormat?.show_duration_label)) {
            let sign = value < 0 ? '-' : ''
            let abs = Math.abs(value)
            let hours = Math.floor(abs)
            let minutes = Math.floor((abs * 60) % 60)
            let seconds = Math.floor((abs * 3600) % 60)
            if (hours > 0) {
              return `${sign}${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
            }
            return `${sign}${minutes}:${String(seconds).padStart(2, '0')}`
          }
          return value
        }
      },
      // Shorthand — formats using the primary data type (used by tooltip)
      formatValue () {
        return (value) => this.formatValueForType(this.yAxisDataType, value)
      },
      // Maps each axis index to its data type for label formatting
      // e.g. ['money', 'number', 'percentage'] — one entry per axis
      axisDataTypes () {
        let types = {}
        for (let [idx, axis,] of Object.entries(this.axisAssignments)) {
          if (types[axis] === undefined) {
            types[axis] = this.yAxisDataTypes[idx] || this.yAxisDataType
          }
        }
        let maxAxis = Math.max(0, ...Object.keys(types).map(Number))
        let result = []
        for (let i = 0; i <= maxAxis; i++) {
          result.push(types[i] || this.yAxisDataTypes[0] || this.yAxisDataType)
        }
        if (result.length === 0) {
          result.push(this.yAxisDataTypes[0] || this.yAxisDataType)
        }
        return result
      },
      // Maps each axis index to its DataFormat config
      axisDataFormats () {
        let formats = {}
        for (let [idx, axis,] of Object.entries(this.axisAssignments)) {
          if (formats[axis] === undefined) {
            formats[axis] = this.yAxisDataFormats[idx] || null
          }
        }
        let maxAxis = Math.max(0, ...Object.keys(formats).map(Number))
        let result = []
        for (let i = 0; i <= maxAxis; i++) {
          result.push(formats[i] || this.yAxisDataFormats[0] || null)
        }
        if (result.length === 0) {
          result.push(this.yAxisDataFormats[0] || null)
        }
        return result
      },
      // When 3+ axes are needed, switch to stacked sub-charts (feature-flagged)
      useStackedLayout () {
        return this.$hasFeatureFlag('STACKED_LINE_CHARTS')
          && this.hasMultipleAxes
          && this.axisDataTypes.length > 2
      },
      // Widest y-axis label across all axes (px), used to align stacked grids
      maxAxisLabelWidth () {
        let canvas = document.createElement('canvas')
        let ctx = canvas.getContext('2d')
        ctx.font = '12px sans-serif'
        let maxWidth = 0
        for (let i = 0; i < this.axisDataTypes.length; i++) {
          let axisMax = 0
          for (let [measureIdx, axisIdx,] of Object.entries(this.axisAssignments)) {
            if (Number(axisIdx) === i) {
              let val = this.measureMaxValues[measureIdx] || 0
              if (val > axisMax) axisMax = val
            }
          }
          let label = String(
            this.formatValueForType(this.axisDataTypes[i], axisMax, this.axisDataFormats[i])
          )
          let width = ctx.measureText(label).width
          if (width > maxWidth) maxWidth = width
        }
        return Math.ceil(maxWidth)
      },
      // ECharts yAxis config for standard (single/dual) mode
      yAxisConfig () {
        if (!this.hasMultipleAxes) {
          return {
            type: 'value',
            axisLabel: {
              margin: 15,
              color: '#B2B1B6',
              formatter: (value) => {
                if (value === 0) return ''
                return this.formatValueForType(this.axisDataTypes[0], value, this.axisDataFormats[0])
              },
            },
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed',
                color: '#E0E6F1',
              },
            },
          }
        }
        // Dual axis — left and right with independent scales
        return this.axisDataTypes.map((dataType, idx) => ({
          type: 'value',
          position: idx === 0 ? 'left' : 'right',
          axisLabel: {
            margin: 15,
            color: '#B2B1B6',
            formatter: (value) => {
              if (value === 0) return ''
              return this.formatValueForType(dataType, value, this.axisDataFormats[idx])
            },
          },
          splitLine: {
            show: idx === 0,
            lineStyle: {
              type: 'dashed',
              color: '#E0E6F1',
            },
          },
        }))
      },
      tooltipConfig () {
        return {
          trigger: 'item',
          backgroundColor: '#28272A',
          borderColor: '#333333',
          textStyle: {
            color: '#FFFFFF',
          },
          axisPointer: {
            type: 'line',
            snap: true,
            animation: true,
            lineStyle: {
              color: '#6E7079',
              width: 1,
              type: 'dashed',
            },
          },
          confine: true,
          formatter: (tooltipParam) => {
            const param = Array.isArray(tooltipParam) ? tooltipParam[0] : tooltipParam
            const hoveredLine = this.data.find((line) => line.id === param.seriesId)
            if (!hoveredLine) return ''
            const date =
              param.axisValueLabel ||
              param.axisValue ||
              this.xAxisLabels[param.dataIndex] ||
              ''
            const groupLines = this.data.filter(
              (line) => line.groupLabel === hoveredLine.groupLabel
            )

            let result = `<div style="text-align:center;font-size:12px;color:#FFFFFF">${date}</div><br/>`
            result += `<div style="display:flex;align-items:center;gap:8px;font-size:12px;color:#FFFFFF;font-weight:600;padding-bottom:8px">
              <span style="display:inline-block;width:10px;height:10px;border-radius:999px;background:${hoveredLine.color}"></span>
              ${hoveredLine.groupLabel}
            </div>`

            groupLines.forEach((line) => {
              const lineValue = line.data[param.dataIndex]
              if (lineValue === null || lineValue === undefined) {
                return
              }

              const lineType = this.yAxisDataTypes[line.measureIndex] || this.yAxisDataType
              const lineFormat = this.yAxisDataFormats[line.measureIndex] || null
              const displayValue = line.formatted_data?.[param.dataIndex] ||
                this.formatValueForType(lineType, lineValue, lineFormat)

              result += `<div style="display:flex;justify-content:space-between;gap:12px;font-size:11px;color:#FFFFFF">
                <span style="display:flex;align-items:center;gap:8px">
                  <span style="display:inline-block;width:16px;height:0;border-top:2px ${line.lineStyleType} ${line.color}"></span>
                  ${line.measureLabel}:
                </span>
                <span style="text-align:right">${displayValue}</span>
              </div>`
            })

            return result
          },
        }
      },
      seriesConfig () {
        return this.data.map(line => {
          let axisIdx = (this.hasMultipleAxes && line.measureIndex !== undefined)
            ? (this.axisAssignments[line.measureIndex] || 0)
            : 0
          return {
            id: line.id,
            name: `${line.groupLabel} - ${line.measureLabel}`,
            type: 'line',
            data: line.data,
            xAxisIndex: this.useStackedLayout ? axisIdx : 0,
            yAxisIndex: axisIdx,
            triggerLineEvent: true,
            showSymbol: true,
            symbolSize: 8,
            lineStyle: {
              width: 2,
              color: line.color,
              type: line.lineStyleType,
              opacity: line.opacity,
            },
            itemStyle: {
              color: line.color,
              opacity: line.opacity,
            },
            smooth: false,
          }
        })
      },
      chartOptions () {
        if (this.useStackedLayout) {
          return this.stackedChartOptions
        }
        return this.standardChartOptions
      },
      standardChartOptions () {
        return {
          tooltip: this.tooltipConfig,
          grid: {
            left: '3%',
            right: '5%',
            bottom: '5%',
            top: '5%',
            containLabel: true,
          },
          xAxis: {
            type: 'category',
            data: this.xAxisLabels,
            boundaryGap: false,
            axisLine: { show: true, },
            axisTick: { show: true, },
            splitLine: { show: false, },
            axisLabel: {
              rotate: 45,
              margin: 15,
              color: '#7E7D84',
            },
            axisPointer: {
              show: true,
              snap: true,
              lineStyle: {
                color: '#6E7079',
                width: 1,
                type: 'dashed',
              },
              label: {
                show: true,
                backgroundColor: '#6E7079',
              },
            },
          },
          yAxis: this.yAxisConfig,
          series: this.seriesConfig,
        }
      },
      stackedChartOptions () {
        let numAxes = this.axisDataTypes.length
        let chartHeight = Math.floor(75 / numAxes)
        let gap = 5
        let isLast = (idx) => idx === numAxes - 1

        let grids = this.axisDataTypes.map((_, idx) => ({
          left: '3%',
          right: '5%',
          top: `${5 + idx * (chartHeight + gap)}%`,
          height: `${chartHeight}%`,
          containLabel: true,
        }))

        let xAxes = this.axisDataTypes.map((_, idx) => ({
          type: 'category',
          data: this.xAxisLabels,
          gridIndex: idx,
          boundaryGap: false,
          axisLine: { show: isLast(idx), },
          axisTick: { show: isLast(idx), },
          splitLine: { show: false, },
          axisLabel: isLast(idx) ? {
            rotate: 45,
            margin: 15,
            color: '#7E7D84',
          } : { show: false, },
          axisPointer: {
            show: true,
            snap: true,
            lineStyle: {
              color: '#6E7079',
              width: 1,
              type: 'dashed',
            },
            label: {
              show: isLast(idx),
              backgroundColor: '#6E7079',
            },
          },
        }))

        let labelWidth = this.maxAxisLabelWidth
        let yAxes = this.axisDataTypes.map((dataType, idx) => ({
          type: 'value',
          gridIndex: idx,
          axisLabel: {
            width: labelWidth,
            overflow: 'none',
            margin: 15,
            color: '#B2B1B6',
            formatter: (value) => {
              if (value === 0) return ''
              return this.formatValueForType(dataType, value, this.axisDataFormats[idx])
            },
          },
          splitLine: {
            show: true,
            lineStyle: {
              type: 'dashed',
              color: '#E0E6F1',
            },
          },
        }))

        return {
          axisPointer: {
            link: [{ xAxisIndex: 'all', },],
          },
          tooltip: this.tooltipConfig,
          grid: grids,
          xAxis: xAxes,
          yAxis: yAxes,
          series: this.seriesConfig,
        }
      },
    },
    watch: {
      data: {
        handler () {
          this.$nextTick(() => {
            if (this.chartInstance) {
              this.chartInstance.setOption(this.chartOptions, true)
            }
          })
        },
        deep: true,
        immediate: true,
      },
      xAxisLabels: {
        handler (newLabels) {
          this.$nextTick(() => {
            if (this.chartInstance) {
              const options = this.chartOptions
              options.xAxis.data = newLabels
              this.chartInstance.setOption(options, true)
            }
          })
        },
        immediate: true,
      },
      isContainerExpanded: {
        handler () {
          this.$nextTick(() => {
            this.resizeChart()
          })
        },
      },
    },
    mounted () {
      this.createChart()
      window.addEventListener('resize', this.resizeChart)
    },
    beforeUnmount () {
      if (this.chartInstance) {
        this.chartInstance.dispose()
      }
      window.removeEventListener('resize', this.resizeChart)
    },
    methods: {
      createChart () {
        this.chartInstance = echarts.init(this.$refs.lineChart)
        this.chartInstance.setOption(this.chartOptions)
      },
      updateChart () {
        if (this.chartInstance) {
          this.chartInstance.setOption(this.chartOptions, true)
        }
      },
      resizeChart () {
        if (this.chartInstance) {
          this.chartInstance.resize()
        }
      },
    },
  }
</script>

<style>
.line-chart-container {
  width: 100%;
  height: 100%;
  display: block;
}
</style>
